import React from 'react';

import { useTranslation } from 'react-i18next';

import { useGetOpenChangeTariffPopup } from '@entities/tariff/model';
import { WidgetCard, WidgetCardsList } from '@entities/widget-card';
import { useMetricTracker } from '@entities/widget-card/model';
import { useAddFreeWidget, useGetWidgetsListQuery } from '@entities/widget-shop/api/hooks';
import { useWidgetModals, WidgetModalController } from '@features';
import { Flex, UiButton } from '@shared/ui';

import cls from './WidgetShop.module.css';
import { WidgetShopSkeleton } from '../WidgetShopSkeleton';

export const WidgetsShop = () => {
    const { t } = useTranslation();
    const { data, isPending, isSuccess } = useGetWidgetsListQuery();
    const { pendingWidgetId, handleAddFreeWidget } = useAddFreeWidget();
    const { trackers } = useMetricTracker();
    const openChangeTariffPopupMutation = useGetOpenChangeTariffPopup();
    const { openDetails, openOnlyAdmin, openPayment } = useWidgetModals();

    if (isPending) {
        return <WidgetShopSkeleton />;
    }

    return (
        <>
            <WidgetModalController fromWidgetPage />
            {isSuccess && (
                <div className={cls.listsWrapper}>
                    {data?.categories?.map((listItem) => (
                        <WidgetCardsList key={listItem.id} title={listItem.title}>
                            {listItem?.widgetsList?.map((widget) => (
                                <WidgetCard
                                    key={widget.id}
                                    {...widget}
                                    actionBtnGroupSlot={
                                        <Flex gap={12}>
                                            {widget.availableActionType === 'buy' && (
                                                <UiButton
                                                    btnText={t('WidgetCard.btn.buy')}
                                                    disabled={pendingWidgetId === widget.id}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        trackers.connect({ widget });

                                                        if (
                                                            widget.isFree &&
                                                            widget.status === 'unavailable'
                                                        ) {
                                                            openChangeTariffPopupMutation.mutate({
                                                                widgetId: widget.id,
                                                                notMyCompany:
                                                                    data?.context?.notMyCompany ??
                                                                    false,
                                                            });
                                                            return;
                                                        }
                                                        if (widget.isFree) {
                                                            handleAddFreeWidget(widget.id);
                                                        }
                                                        if (
                                                            !widget.isFree &&
                                                            data?.context?.notMyCompany
                                                        ) {
                                                            openOnlyAdmin(widget.id);
                                                            return;
                                                        }
                                                        if (
                                                            !widget.isFree &&
                                                            !data?.context?.notMyCompany
                                                        ) {
                                                            openPayment(widget.id);
                                                        }
                                                    }}
                                                />
                                            )}
                                            {widget.availableActionType === 'prolong' &&
                                                !widget.isFree && (
                                                    <UiButton
                                                        btnText={t('WidgetCard.btn.prolong')}
                                                        className={cls.increaseTariffBtn}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            trackers.prolong({
                                                                widget,
                                                            });
                                                            openPayment(widget.id);
                                                        }}
                                                    />
                                                )}
                                            {widget.availableActionType === 'upgrade_tariff' && (
                                                <UiButton
                                                    btnText={t('WidgetCard.btn.upgrade_tariff')}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openChangeTariffPopupMutation.mutate({
                                                            widgetId: widget.id,
                                                            notMyCompany:
                                                                data?.context?.notMyCompany ??
                                                                false,
                                                        });
                                                    }}
                                                />
                                            )}
                                            {widget.canViewFullDescription && (
                                                <UiButton
                                                    btnText={t('WidgetCard.btn.more')}
                                                    variant="text"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openDetails(widget.id);
                                                        trackers.viewFullDescription({ widget });
                                                    }}
                                                />
                                            )}
                                        </Flex>
                                    }
                                    onCardClick={() => {
                                        if (widget.canViewFullDescription) {
                                            openDetails(widget.id);
                                            trackers.viewFullDescription({ widget });
                                        }
                                    }}
                                />
                            ))}
                        </WidgetCardsList>
                    ))}
                </div>
            )}
        </>
    );
};
